import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
import { CoreContext } from 'context/CoreContext';
import { useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Forward } from 'services/cobranca';
import { exposeStrapiError } from 'utils';
import Wrapper from '../Wrapper';
import {
  AvisoBox,
  AvisoTexto,
  AvisoTitulo,
  CompanyButtonContainer,
  ForwardContainer,
  ResumoContainer,
  ResumoItem,
  ResumoLabel,
  ResumoValue,
} from './styled';

// Os dois destinos da esteira juridica. `status` e o valor que trafega na API
// — sem acento e em minusculas, como todos os outros —, `label` e o rotulo de
// tela.
export const BILLING_STATUS_LABELS = {
  notificacao: 'Notificação',
  execucao: 'Execução',
};

const DESTINATIONS = {
  notificacao: {
    label: 'Notificação',
    color: '#FF9500',
    action: 'Encaminhar para Notificação',
    // Vem da Recuperacao, onde a regua ainda esta ativa: e aqui que os avisos
    // programados sao cancelados.
    warning:
      'O contrato sai da cobrança automática e vai para a etapa extrajudicial: a régua para de disparar e todos os avisos que estavam programados são cancelados agora. Eles não são reabertos depois.',
  },
  execucao: {
    label: 'Execução',
    color: '#FF3B30',
    action: 'Encaminhar para Execução',
    warning:
      'O contrato sai da cobrança automática: a régua para de disparar e todos os avisos que estavam programados são cancelados agora. Eles não são reabertos depois.',
  },
};

// A rota devolve 400 quando o contrato ja esta no status pedido — tipicamente
// clique duplo, ou dois operadores na mesma linha. Nao e falha: o resultado
// desejado ja vale, entao avisa e recarrega em vez de pintar de vermelho.
const alreadyInStatus = (result) =>
  `${result?.error?.message || ''}`.toLowerCase().includes('já está');

export default function BillingForward({ dados, destination, reload }) {
  const { setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const target = DESTINATIONS[destination] || DESTINATIONS.execucao;
  const cobrancaId = dados?.id;

  const close = () => setModal(null);

  const formItems = [
    {
      ref: 'observacao',
      placeholder: 'Observação (opcional)',
      type: 'text',
      full: true,
      multiline: true,
      maxRows: 4,
      height: 100,
    },
  ];

  const confirm = async () => {
    if (!cobrancaId) {
      toast.error('Não foi possível encaminhar: cobrança não identificada.');
      return;
    }
    // getForm(true) pula a validacao de obrigatorios — aqui nao ha nenhum, a
    // observacao e livre.
    const form = formRef.current?.getForm?.(true) || {};

    setLoading(true);
    try {
      const result = await Forward(cobrancaId, destination, {
        data: { observacao: form?.observacao || null },
      });

      if (result?.error) {
        if (alreadyInStatus(result)) {
          toast.warning(`Contrato já está em ${target.label}.`);
          close();
          reload?.();
          return;
        }
        exposeStrapiError(result);
        return;
      }

      // `agendamentos_cancelados` e a confirmacao visivel de que o cliente
      // parou de receber cobranca automatica. Zero e legitimo — contrato que
      // nao tinha nada na fila —, e ai a segunda frase nao aparece.
      const cancelados = result?.agendamentos_cancelados || 0;
      toast.success(
        `Contrato enviado para ${target.label}.` +
          (cancelados
            ? ` ${cancelados} aviso${cancelados > 1 ? 's' : ''} programado${
                cancelados > 1 ? 's' : ''
              } ${cancelados > 1 ? 'foram cancelados' : 'foi cancelado'}.`
            : '')
      );
      close();
      reload?.();
    } catch (err) {
      console.error('Erro ao encaminhar cobrança:', err);
      toast.error('Erro ao encaminhar o contrato');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper title={target.action} onClose={close}>
      <ForwardContainer>
        <ResumoContainer>
          <ResumoItem>
            <ResumoLabel>Contrato</ResumoLabel>
            <ResumoValue>{dados?.contrato || cobrancaId || '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Cliente</ResumoLabel>
            <ResumoValue>{dados?.customer || '-'}</ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Status atual</ResumoLabel>
            <ResumoValue>
              {BILLING_STATUS_LABELS[dados?.status] || dados?.status || '-'}
            </ResumoValue>
          </ResumoItem>
          <ResumoItem>
            <ResumoLabel>Atraso</ResumoLabel>
            <ResumoValue>
              {dados?.maior_atraso ? `${dados.maior_atraso} dias` : '-'}
            </ResumoValue>
          </ResumoItem>
        </ResumoContainer>

        <AvisoBox $color={target.color}>
          <AvisoTitulo>Confirme antes de seguir</AvisoTitulo>
          <AvisoTexto>{target.warning}</AvisoTexto>
        </AvisoBox>

        <FormCore ref={formRef} formItems={formItems} />

        <CompanyButtonContainer>
          <Button color="lightgrey" onClick={close} nospace>
            Cancelar
          </Button>
          <Button color="blue" onClick={confirm} disabled={loading} nospace>
            {loading ? 'Encaminhando...' : target.action}
          </Button>
        </CompanyButtonContainer>
      </ForwardContainer>
    </Wrapper>
  );
}
